import { useState, useRef, useEffect, useId } from "react";
import { t } from "@chuzi/shared/config";
import type { RealmId, StoryVisibilityTier } from "@chuzi/shared/types";
import type { NavLevel, NavTier } from "../lib/cosmos-nav-state";
import type { RealmScopeMode } from "./realm-scope-selector";
import { CreateFab } from "./create-fab";

interface CosmosRadialNavProps {
  realm: RealmId;
  scopeMode: RealmScopeMode;
  onScopeChange: (mode: RealmScopeMode) => void;
  onCreateClick: () => void;
  showCreate?: boolean;
  navLabel: string;
  navLevel: NavLevel;
  navItemIndex: number;
  navItemCount: number;
  canDrill: boolean;
  onNavLeft: () => void;
  onNavRight: () => void;
  onNavDrill: () => void;
  onNavBack: () => void;
  onNavCenter?: () => void;
  onNavKeyAction?: () => void;
  showActions?: boolean;
  keyAction?: "edit" | "launch";
  canDelete?: boolean;
  onDelete?: () => void;
  onPurge?: () => void;
  onPromote?: () => void;
  onEdit?: () => void;
  onLaunch?: () => void;
  onPublish?: () => void;
  onUnpublish?: () => void;
  onRetract?: () => void;
  storyTier?: StoryVisibilityTier;
  currentTier?: NavTier;
  disabled?: boolean;
}

type ConfirmTarget = "delete" | "unpublish" | "purge" | null;

type NavButton = {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  lit?: boolean;
  destructive?: boolean;
  confirming?: boolean;
};

export function CosmosRadialNav({
  realm,
  scopeMode,
  onScopeChange,
  onCreateClick,
  showCreate = true,
  navLabel,
  navLevel,
  navItemCount,
  canDrill,
  onNavLeft,
  onNavRight,
  onNavDrill,
  onNavBack,
  onNavCenter,
  onNavKeyAction,
  showActions = false,
  canDelete = false,
  onDelete,
  onPurge,
  onPromote,
  onEdit,
  onLaunch,
  onPublish,
  onUnpublish,
  onRetract,
  storyTier,
  currentTier,
  disabled,
}: CosmosRadialNavProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [focusedBtn, setFocusedBtn] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget>(null);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animId = useId().replace(/:/g, "");

  const isWilds = realm === "wilds";
  const accent = isWilds ? "#78c878" : "#7eb8ff";
  const accentRgba = isWilds ? "rgba(120,200,120," : "rgba(126,184,255,";
  const btnBg = isWilds ? "rgba(20,40,20,0.92)" : "rgba(10,16,32,0.92)";
  const btnBorder = isWilds ? "rgba(120,200,120,0.4)" : "rgba(126,184,255,0.4)";
  const btnBorderHover = isWilds ? "rgba(120,200,120,0.7)" : "rgba(126,184,255,0.7)";

  const canLeft = navItemCount > 1;
  const canRight = navItemCount > 1;

  const isJettisonTier = currentTier === "jettisoned";
  const tier = isJettisonTier ? "private" : (storyTier ?? "private");
  const actionCanLaunch = !!(onLaunch && tier === "worldwide" && !isJettisonTier);
  const actionCanUnpublish = !!(onUnpublish && tier === "worldwide" && !isJettisonTier);
  const actionCanRetract = !!(onRetract && tier === "limited" && !isJettisonTier);
  const actionCanPromote = !!(onPromote && isJettisonTier);
  const actionShowDelete = canDelete && !actionCanUnpublish && !actionCanRetract && !isJettisonTier;
  const actionShowPurge = !!(onPurge && isJettisonTier && canDelete);

  useEffect(() => {
    return () => {
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
    };
  }, []);

  function handleConfirmableClick(
    target: "delete" | "unpublish" | "purge",
    action: () => void,
  ) {
    if (confirmTarget === target) {
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
      confirmTimer.current = null;
      setConfirmTarget(null);
      action();
    } else {
      setConfirmTarget(target);
      confirmTimer.current = setTimeout(() => {
        setConfirmTarget(null);
        confirmTimer.current = null;
      }, 3000);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      e.stopPropagation();
      onNavLeft();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      e.stopPropagation();
      onNavRight();
    } else if (e.key === "ArrowDown" || e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (canDrill) onNavDrill();
      else if (e.key === "Enter" && onNavKeyAction) onNavKeyAction();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      onNavBack();
    }
  }

  useEffect(() => {
    rootRef.current?.focus();
  }, []);

  const BTN_SIZE = 44;
  const RING_RADIUS = 78;

  const deleteOnClick = actionCanUnpublish
    ? () => handleConfirmableClick("unpublish", onUnpublish!)
    : actionShowPurge && onPurge
      ? () => handleConfirmableClick("purge", onPurge)
      : actionShowDelete && onDelete
        ? () => handleConfirmableClick("delete", onDelete)
        : undefined;

  const deleteConfirming = confirmTarget === (actionCanUnpublish ? "unpublish" : actionShowPurge ? "purge" : "delete");

  const promoteLabel = t(realm, "jettison_promote", "Promote");
  const purgeLabel = t(realm, "jettison_purge_story", "Purge");

  type ArcButton = NavButton & { angle: number };

  const arcButtons: ArcButton[] = [
    {
      id: "mine",
      label: "mine",
      icon: <PersonIcon color={accent} />,
      onClick: disabled ? undefined : () => onScopeChange("mine"),
      active: scopeMode === "mine",
      angle: 160,
    },
    {
      id: "all",
      label: "all",
      icon: <InfinityIcon color={accent} />,
      onClick: disabled ? undefined : () => onScopeChange("all"),
      active: scopeMode === "all",
      angle: 200,
    },
    {
      id: "edit",
      label: "edit",
      icon: <PencilIcon color={accent} />,
      onClick: showActions && onEdit ? onEdit : undefined,
      active: showActions && !!onEdit,
      angle: 35,
    },
    {
      id: "watch",
      label: actionCanPromote ? promoteLabel : "watch",
      icon: actionCanPromote ? <PromoteIcon color={accent} /> : <EyeIcon color={accent} />,
      onClick: showActions && actionCanPromote
        ? onPromote
        : showActions && actionCanLaunch
          ? onLaunch
          : undefined,
      active: showActions && (actionCanPromote || !!onLaunch),
      lit: showActions && (actionCanPromote || actionCanLaunch),
      angle: 0,
    },
    {
      id: "delete",
      label: deleteConfirming ? "confirm?" : actionShowPurge ? purgeLabel : "delete",
      icon: <TrashIcon color={deleteConfirming ? "#ff6b6b" : accent} />,
      onClick: showActions ? deleteOnClick : undefined,
      active: showActions && (actionShowDelete || actionCanUnpublish || actionShowPurge),
      destructive: true,
      confirming: deleteConfirming,
      angle: -35,
    },
  ];

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{
        position: "absolute",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 110,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0,
        outline: "none",
        animation: "radialNavFadeIn 0.5s ease-out",
      }}
    >
      {/* Nav layer indicator */}
      <NavLayerIndicator
        label={navLabel}
        level={navLevel}
        canLeft={canLeft}
        canRight={canRight}
        canDrill={canDrill}
        onLeft={onNavLeft}
        onRight={onNavRight}
        onDrill={onNavDrill}
        onBack={onNavBack}
        onCenter={onNavCenter}
        accent={accent}
        accentRgba={accentRgba}
        animId={animId}
      />

      {/* Button cluster: circular arc around CREATE */}
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* CREATE at center — defines flow size */}
        {showCreate ? (
          <CreateFab realm={realm} onClick={onCreateClick} disabled={disabled} embedded compact={false} />
        ) : (
          <div style={{ width: 100, height: 100 }} />
        )}

        {/* Nav buttons on circular arc */}
        {arcButtons.map((btn) => {
          const rad = (btn.angle * Math.PI) / 180;
          const x = Math.cos(rad) * RING_RADIUS;
          const y = -Math.sin(rad) * RING_RADIUS;
          const isLeft = btn.angle > 90 || btn.angle < -90;
          return (
            <div
              key={btn.id}
              style={{
                position: "absolute",
                left: `calc(50% + ${x}px)`,
                top: `calc(50px + ${y}px)`,
                transform: "translate(-50%, -50%)",
                zIndex: 1,
              }}
            >
              <NavIconButton
                id={btn.id}
                label={btn.label}
                icon={btn.icon}
                size={BTN_SIZE}
                bg={btnBg}
                border={btnBorder}
                borderHover={btnBorderHover}
                accent={accent}
                accentRgba={accentRgba}
                active={btn.active}
                lit={btn.lit}
                destructive={btn.destructive}
                confirming={btn.confirming}
                clickable={!!btn.onClick}
                focused={focusedBtn === btn.id}
                onFocus={() => setFocusedBtn(btn.id)}
                onBlur={() => setFocusedBtn(null)}
                onClick={btn.onClick}
                disabled={disabled}
                labelSide={isLeft ? "left" : "right"}
              />
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes radialNavFadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(16px) scale(0.92); }
          to { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
        }
        @keyframes ${animId}-chevA {
          0%, 45% { opacity: 1; }
          50%, 95% { opacity: 0.2; }
          100% { opacity: 1; }
        }
        @keyframes ${animId}-chevB {
          0%, 45% { opacity: 0.2; }
          50%, 95% { opacity: 1; }
          100% { opacity: 0.2; }
        }
      `}</style>
    </div>
  );
}

/* ─── Nav Layer Indicator ─── */

function NavLayerIndicator({
  label,
  level,
  canLeft,
  canRight,
  canDrill,
  onLeft,
  onRight,
  onDrill,
  onBack,
  onCenter,
  accent,
  accentRgba,
  animId,
}: {
  label: string;
  level: NavLevel;
  canLeft: boolean;
  canRight: boolean;
  canDrill: boolean;
  onLeft: () => void;
  onRight: () => void;
  onDrill: () => void;
  onBack: () => void;
  onCenter?: () => void;
  accent: string;
  accentRgba: string;
  animId: string;
}) {
  const showSideArrows = canLeft || canRight;
  const showDownArrow = canDrill;
  const showUpArrow = level !== "nebula";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        marginBottom: 8,
      }}
    >
      {/* Up arrow (back) */}
      {showUpArrow ? (
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 4,
            opacity: 0.8,
          }}
          aria-label="Back"
        >
          <DoubleChevron direction="up" color={accent} animId={animId} />
        </button>
      ) : null}

      {/* Row: [left arrows] LABEL [right arrows] */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "4px 12px",
          borderRadius: 20,
          background: "rgba(4, 8, 18, 0.75)",
          backdropFilter: "blur(8px)",
          border: `1px solid ${accentRgba}0.2)`,
          minHeight: 32,
        }}
      >
        {showSideArrows ? (
          <button
            type="button"
            onClick={onLeft}
            disabled={!canLeft}
            style={{
              background: "none",
              border: "none",
              cursor: canLeft ? "pointer" : "default",
              padding: 2,
              opacity: canLeft ? 0.9 : 0.2,
            }}
            aria-label="Previous"
          >
            <DoubleChevron direction="left" color={accent} animId={animId} />
          </button>
        ) : null}

        <span
          role={onCenter ? "button" : undefined}
          tabIndex={onCenter ? 0 : undefined}
          onClick={onCenter ? () => onCenter() : undefined}
          onKeyDown={
            onCenter
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onCenter();
                  }
                }
              : undefined
          }
          style={{
            color: "#e8f0ff",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            minWidth: 60,
            textAlign: "center",
            textShadow: `0 0 8px ${accentRgba}0.4)`,
            cursor: onCenter ? "pointer" : "default",
          }}
        >
          {label || "\u00A0"}
        </span>

        {showSideArrows ? (
          <button
            type="button"
            onClick={onRight}
            disabled={!canRight}
            style={{
              background: "none",
              border: "none",
              cursor: canRight ? "pointer" : "default",
              padding: 2,
              opacity: canRight ? 0.9 : 0.2,
            }}
            aria-label="Next"
          >
            <DoubleChevron direction="right" color={accent} animId={animId} />
          </button>
        ) : null}
      </div>

      {/* Down arrow (drill) */}
      {showDownArrow ? (
        <button
          type="button"
          onClick={onDrill}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 4,
            opacity: 0.8,
          }}
          aria-label="Drill down"
        >
          <DoubleChevron direction="down" color={accent} animId={animId} />
        </button>
      ) : null}
    </div>
  );
}

/* ─── Double Chevron (animated) ─── */

function DoubleChevron({
  direction,
  color,
  animId,
}: {
  direction: "left" | "right" | "up" | "down";
  color: string;
  animId: string;
}) {
  const rotation =
    direction === "right" ? 90
    : direction === "down" ? 180
    : direction === "left" ? 270
    : 0;

  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 40 48"
      style={{
        transform: `rotate(${rotation}deg)`,
        overflow: "visible",
      }}
      aria-hidden
    >
      <polyline
        style={{ animation: `${animId}-chevA 0.7s ease-in-out infinite` }}
        points="8,18 20,8 32,18"
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="3 4"
      />
      <polyline
        style={{ animation: `${animId}-chevB 0.7s ease-in-out infinite` }}
        points="8,32 20,22 32,32"
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="3 4"
      />
    </svg>
  );
}

/* ─── Nav Icon Button ─── */

function NavIconButton({
  id,
  label,
  icon,
  size,
  bg,
  border,
  borderHover,
  accent,
  accentRgba,
  active,
  lit,
  destructive,
  confirming,
  clickable,
  focused,
  onFocus,
  onBlur,
  onClick,
  disabled,
  labelSide = "right",
}: {
  id: string;
  label: string;
  icon: React.ReactNode;
  size: number;
  bg: string;
  border: string;
  borderHover: string;
  accent: string;
  accentRgba: string;
  active?: boolean;
  lit?: boolean;
  destructive?: boolean;
  confirming?: boolean;
  clickable: boolean;
  focused: boolean;
  onFocus: () => void;
  onBlur: () => void;
  onClick?: () => void;
  disabled?: boolean;
  labelSide?: "left" | "right";
}) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const showLabel = hovered || focused;

  const isLit = lit || active;

  const activeBorder = confirming
    ? "rgba(255,80,80,0.7)"
    : isLit
      ? (destructive ? "rgba(255,80,80,0.5)" : accent)
      : focused ? borderHover : border;

  const activeGlow = confirming
    ? "0 0 12px rgba(255,80,80,0.4)"
    : isLit && !destructive
      ? `0 0 12px ${accentRgba}0.4)`
      : !clickable
        ? `0 0 4px ${accentRgba}0.12)`
        : "none";

  const slideOffset = showLabel ? 0 : labelSide === "left" ? 4 : -4;
  const labelStyle =
    labelSide === "left"
      ? {
          right: size + 8,
          top: "50%",
          transform: `translateY(calc(-50% + ${slideOffset}px))`,
        }
      : {
          left: size + 8,
          top: "50%",
          transform: `translateY(calc(-50% + ${slideOffset}px))`,
        };

  return (
    <div style={{ position: "relative", width: size, height: size, overflow: "visible" }}>
      <button
        type="button"
        tabIndex={0}
        aria-label={label}
        onClick={() => {
          if (!clickable || disabled) return;
          setPressed(true);
          setTimeout(() => setPressed(false), 150);
          onClick?.();
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled || !clickable}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          border: `1.5px solid ${activeBorder}`,
          background: confirming ? "rgba(255,60,60,0.12)" : bg,
          cursor: clickable && !disabled ? "pointer" : "default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          backdropFilter: "blur(8px)",
          boxShadow: activeGlow,
          transition: "border-color 200ms, box-shadow 200ms, transform 150ms, opacity 200ms, filter 200ms",
          transform: pressed ? "scale(0.9)" : hovered ? "scale(1.1)" : "none",
          opacity: clickable ? 1 : 0.55,
          filter: clickable ? "none" : "brightness(0.45)",
        }}
      >
        {icon}
      </button>

      {/* Floating label */}
      <div
        style={{
          position: "absolute",
          ...labelStyle,
          opacity: showLabel ? 1 : 0,
          pointerEvents: "none",
          transition: "opacity 200ms ease, transform 200ms ease",
          whiteSpace: "nowrap",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: confirming ? "#ff6b6b" : "#e8f0ff",
          textShadow: `0 1px 6px rgba(0,0,0,0.8)`,
          padding: "3px 8px",
          borderRadius: 8,
          background: "rgba(4, 8, 18, 0.85)",
          backdropFilter: "blur(6px)",
          border: `1px solid ${accentRgba}0.2)`,
        }}
      >
        {label}
      </div>
    </div>
  );
}

/* ─── Icon Components ─── */

function PersonIcon({ color }: { color: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="5" r="2.5" stroke={color} strokeWidth="1.3" />
      <path
        d="M3.5 14c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5"
        stroke={color}
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function InfinityIcon({ color }: { color: string }) {
  return (
    <svg width="20" height="16" viewBox="0 0 20 12" fill="none">
      <path
        d="M5 6c0-1.7 1.3-3 3-3 2.2 0 3.3 1.8 4.5 3-1.2 1.2-2.3 3-4.5 3-1.7 0-3-1.3-3-3zm10 0c0 1.7-1.3 3-3 3-2.2 0-3.3-1.8-4.5-3 1.2-1.2 2.3-3 4.5-3 1.7 0 3 1.3 3 3z"
        stroke={color}
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PencilIcon({ color }: { color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M11.5 1.5l3 3-9 9H2.5v-3l9-9zM10 3l3 3"
        stroke={color}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EyeIcon({ color }: { color: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
      <path
        d="M1.5 8s2.5-4.5 6.5-4.5S14.5 8 14.5 8s-2.5 4.5-6.5 4.5S1.5 8 1.5 8z"
        stroke={color}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="2" stroke={color} strokeWidth="1.2" />
    </svg>
  );
}

function TrashIcon({ color }: { color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M5.5 1.5h5M2 3.5h12M3.5 3.5l.7 9.1a1.5 1.5 0 001.5 1.4h4.6a1.5 1.5 0 001.5-1.4l.7-9.1M6.5 6.5v4M9.5 6.5v4"
        stroke={color}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PromoteIcon({ color }: { color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 12V4M8 4L5 7M8 4l3 3"
        stroke={color}
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
